import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { walletsApi, Wallet, WalletCreate, WalletUpdate, WalletType } from '../services/wallets';
import { format } from 'date-fns';

const WalletsPage: React.FC = () => {
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [allWallets, setAllWallets] = useState<Wallet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [walletTypeFilter, setWalletTypeFilter] = useState<WalletType | 'all'>('all');
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<Wallet | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState<WalletCreate>({
    name: '',
    wallet_type: 'CRYPTO',
    active: true,
    crypto_currency: '',
    wallet_address: '',
    bank_details: ''
  });

  const loadWallets = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await walletsApi.getWallets(0, 1000);
      setAllWallets(data);
    } catch (err: any) {
      console.error('Error loading wallets:', err);
      setError(err.response?.data?.detail || 'Error loading wallets');
    } finally {
      setLoading(false);
    }
  };

  const filterWallets = (data: Wallet[], search: string, onlyActive: boolean, type: WalletType | 'all') => {
    let filtered = [...data];
    
    // Search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(wallet => 
        wallet.name?.toLowerCase().includes(searchLower) ||
        wallet.crypto_currency?.toLowerCase().includes(searchLower) ||
        wallet.wallet_address?.toLowerCase().includes(searchLower) ||
        wallet.bank_details?.toLowerCase().includes(searchLower)
      );
    }
    
    // Active filter
    if (onlyActive) {
      filtered = filtered.filter(wallet => wallet.active);
    }
    
    // Type filter
    if (type !== 'all') {
      filtered = filtered.filter(wallet => wallet.wallet_type === type);
    }
    
    setWallets(filtered);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      filterWallets(allWallets, searchTerm, activeOnly, walletTypeFilter);
    }
  };

  useEffect(() => {
    filterWallets(allWallets, searchTerm, activeOnly, walletTypeFilter);
  }, [allWallets, searchTerm, activeOnly, walletTypeFilter]);

  const loadWalletDetails = async (id: number) => {
    try {
      setFormLoading(true);
      const wallet = await walletsApi.getWallet(id);
      setSelectedWallet(wallet);
      return wallet;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error loading wallet data');
      return null;
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    loadWallets();
  }, []);

  const handleOpenWallet = async (walletId: number) => {
    const wallet = await loadWalletDetails(walletId);
    if (wallet) {
      setIsViewMode(false);
    setFormData({
      name: wallet.name,
      wallet_type: wallet.wallet_type as WalletType,
      active: wallet.active,
      crypto_currency: wallet.crypto_currency || '',
      wallet_address: wallet.wallet_address || '',
      bank_details: wallet.bank_details || ''
    });
      setOpenEditDialog(true);
    }
  };

  const handleAddWallet = () => {
    setSelectedWallet(null);
    setIsViewMode(false);
    setFormData({
      name: '',
      wallet_type: 'CRYPTO',
      active: true,
      crypto_currency: '',
      wallet_address: '',
      bank_details: ''
    });
    setOpenEditDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenEditDialog(false);
    setSelectedWallet(null);
    setFormData({
      name: '',
      wallet_type: 'CRYPTO',
      active: true,
      crypto_currency: '',
      wallet_address: '',
      bank_details: ''
    });
  };

  const handleSaveWallet = async () => {
    try {
      setFormLoading(true);
      setError('');

      // Validation
      if (!formData.name.trim()) {
        setError('Wallet name is required');
        return;
      }

      if (formData.wallet_type === 'CRYPTO') {
        if (!formData.crypto_currency?.trim()) {
          setError('Cryptocurrency is required for crypto wallets');
          return;
        }
        if (!formData.wallet_address?.trim()) {
          setError('Wallet address is required for crypto wallets');
          return;
        }
      } else if (formData.wallet_type === 'BANK') {
        if (!formData.bank_details?.trim()) {
          setError('Bank details are required for bank wallets');
          return;
        }
      }

      if (selectedWallet) {
        const updateData: WalletUpdate = {
          name: formData.name,
          wallet_type: formData.wallet_type,
          active: formData.active,
          crypto_currency: formData.crypto_currency || undefined,
          wallet_address: formData.wallet_address || undefined,
          bank_details: formData.bank_details || undefined
        };
        await walletsApi.updateWallet(selectedWallet.id, updateData);
      } else {
        await walletsApi.createWallet(formData);
      }

      handleCloseDialog();
      await loadWallets();
    } catch (err: any) {
      console.error('Error saving wallet:', err);
      setError(err.response?.data?.detail || 'Error saving wallet');
    } finally {
      setFormLoading(false);
    }
  };

  const getWalletTypeLabel = (type: WalletType) => {
    return type === 'CRYPTO' ? 'Crypto' : 'Bank';
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Payment Accounts Registry</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={handleAddWallet}
        >
          Add Wallet
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box mb={3} display="flex" gap={2} flexWrap="wrap">
        <TextField
          placeholder="Search by name, currency, address..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ flexGrow: 1, minWidth: 300 }}
        />
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={walletTypeFilter}
            label="Type"
            onChange={(e) => setWalletTypeFilter(e.target.value as WalletType | 'all')}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="CRYPTO">Crypto</MenuItem>
            <MenuItem value="BANK">Bank</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
          }
          label="Active only"
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Cryptocurrency</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {wallets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No wallets found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              wallets.map((wallet) => (
                <TableRow key={wallet.id} hover>
                  <TableCell>{wallet.id}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        cursor: 'pointer',
                        color: 'primary.main',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => handleOpenWallet(wallet.id)}
                    >
                      {wallet.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getWalletTypeLabel(wallet.wallet_type)}
                      color={wallet.wallet_type === 'CRYPTO' ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {wallet.crypto_currency ? (
                      <Chip label={wallet.crypto_currency} size="small" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={wallet.active ? 'Active' : 'Inactive'}
                      color={wallet.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {format(new Date(wallet.created_at), 'dd.MM.yyyy HH:mm')}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openEditDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedWallet ? 'Edit Wallet' : 'Add Wallet'}
        </DialogTitle>
        <DialogContent>
          {formLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Wallet Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Wallet Type</InputLabel>
                  <Select
                    value={formData.wallet_type}
                    label="Wallet Type"
                    onChange={(e) => {
                      const newType = e.target.value as WalletType;
                      setFormData({
                        ...formData,
                        wallet_type: newType,
                        // Clear fields when changing type
                        crypto_currency: newType === 'CRYPTO' ? formData.crypto_currency : '',
                        wallet_address: newType === 'CRYPTO' ? formData.wallet_address : '',
                        bank_details: newType === 'BANK' ? formData.bank_details : ''
                      });
                    }}
                  >
                    <MenuItem value="CRYPTO">Crypto</MenuItem>
                    <MenuItem value="BANK">Bank</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
              {formData.wallet_type === 'CRYPTO' && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cryptocurrency"
                      value={formData.crypto_currency}
                      onChange={(e) => setFormData({ ...formData, crypto_currency: e.target.value })}
                      placeholder="BTC, ETH, USDT..."
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Wallet Address"
                      value={formData.wallet_address}
                      onChange={(e) => setFormData({ ...formData, wallet_address: e.target.value })}
                      required
                      multiline
                      rows={2}
                    />
                  </Grid>
                </>
              )}
              {formData.wallet_type === 'BANK' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Bank Details"
                    value={formData.bank_details}
                    onChange={(e) => setFormData({ ...formData, bank_details: e.target.value })}
                    required
                    multiline
                    rows={4}
                    placeholder="Bank, BIC, account number, correspondent account, etc."
                  />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={formLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveWallet}
            variant="contained"
            disabled={formLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default WalletsPage;

